import { debounce } from 'lodash-es'
import { useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router'

import { LoadMore } from '@fv/client-components'
import { flattenPages } from '@fv/client-core'
import { userTypes } from '@fv/models'

import { AdminLoading } from '@/components/shared/AdminLoading'
import { AdminSection } from '@/components/shared/AdminSection'
import { AdminSelect } from '@/components/shared/AdminSelect'
import { AdminTextField } from '@/components/shared/AdminTextField'
import { useFetchUsers } from '@/hooks/useFetchUsers'

import { UsersTable } from './UsersTable'

export const UserSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('search') ?? ''
  const userType = searchParams.get('type') ?? ''

  const searchRef = useRef<(value: string) => void>(null)
  searchRef.current = (value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) {
        next.set('search', value)
      } else {
        next.delete('search')
      }
      return next
    })
  }

  const debouncedSearch = useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        searchRef.current?.(e.target.value.trim())
      }, 500),
    [],
  )
  const usersQuery = useFetchUsers(search, 20, userType)
  const users = flattenPages(usersQuery.data?.pages)

  return (
    <AdminSection
      title="User search"
      headerActions={
        <div className="join ml-auto w-96">
          <AdminSelect
            name="userType"
            value={userType}
            onChange={e =>
              setSearchParams(prev => {
                const next = new URLSearchParams(prev)
                if (e.target.value) {
                  next.set('type', e.target.value)
                } else {
                  next.delete('type')
                }
                return next
              })
            }
            options={[
              { text: 'All Types', value: '' },
              ...userTypes
                .filter(t => t !== 'admin')
                .map(t => ({
                  text: t.charAt(0).toUpperCase() + t.slice(1),
                  value: t,
                })),
            ]}
          />
          <AdminTextField
            maxLength={200}
            name="search"
            defaultValue={search}
            onChange={debouncedSearch}
            placeholder="Search..."
          />
        </div>
      }
    >
      {usersQuery.isLoading && <AdminLoading />}
      <UsersTable users={users} />
      <LoadMore
        fetchNextPage={usersQuery.fetchNextPage}
        hasNextPage={usersQuery.hasNextPage}
        isLoading={usersQuery.isFetchingNextPage}
      />
    </AdminSection>
  )
}
